function _typeof(obj) { if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof = function _typeof(obj) { return typeof obj; }; } else { _typeof = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof(obj); }

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); if (staticProps) _defineProperties(Constructor, staticProps); return Constructor; }

import { builtinsByValue } from './builtins';
/**
 * Takes arbitrary javascript objects and encodes them using only JSON-safe objects.
 *
 * Usage:
 *   const encoder = new Encoder();
 *   const result = encoder.encode(myObject);
 *
 * The instance of Encoder remembers objects and symbols that it has seen before, and
 * will encode them with the same ID on subsequent calls to .encode(). By default,
 * IDs are sequentially generated, and if you create a new Encoder instance the IDs
 * will start over at '1' again.
 *
 * You can override the generateId() method to change how IDs are generated, or
 * override internObject() and internValue() to manage the lookup of existing objects
 * yourself.
 */

var Encoder =
/*#__PURE__*/
function () {
  function Encoder() {
    _classCallCheck(this, Encoder);

    this.objectIds = new WeakMap();
    this.symbolIds = new Map();
    this.nextId = 1;
  }
  /**
   * @returns {string} a new id that is unique at least within the
   *   context of this Encoder instance
   */


  _createClass(Encoder, [{
    key: "generateId",
    value: function generateId() {
      var id = this.nextId;
      this.nextId += 1;
      return id.toString();
    }
    /**
     * Retrieves or creates an id for an object, array, or function.
     * At least within the context of this instance of the Encoder, this
     * should return the same value every time it is called for the same in-memory
     * object, and different values when called for different in-memory objects.
     * @param {*} value an object, array, or function
     * @returns {string} the id
     */

  }, {
    key: "internObject",
    value: function internObject(value) {
      var id = this.objectIds.get(value);

      if (!id) {
        id = this.generateId();
        this.objectIds.set(value, id);
      }

      return id;
    }
    /**
     * Retrieves or creates an id for a symbol.
     * At least within the context of this instance of the Encoder, this should
     * return the same value every time it is called for the same symbol, and different values
     * when called for different symbols.
     * @param {*} symbol
     * @returns {string} the id
     */

  }, {
    key: "internSymbol",
    value: function internSymbol(symbol) {
      var id = this.symbolIds.get(symbol);

      if (!id) {
        id = this.generateId();
        this.symbolIds.set(symbol, id);
      }

      return id;
    }
    /**
     * Encodes the given value.
     * @param {*} value
     * @returns {*} the value encoded according to the spec in the README
     */

  }, {
    key: "encode",
    value: function encode(value) {
      var _this = this;

      var refIds = null;

      var _recurse;

      var encodeProp;

      encodeProp = function encodeProp(desc) {
        // eslint-disable-line prefer-const
        if (desc.writable && desc.enumerable && desc.configurable && !desc.get && !desc.set) {
          return _recurse(desc.value);
        }

        var prop = {
          type: 'property'
        };

        if (desc.get) {
          prop.get = _recurse(desc.get);
        }

        if (desc.set) {
          prop.set = _recurse(desc.set);
        }

        if (!desc.get && !desc.set) {
          prop.value = _recurse(desc.value);
        }

        if (desc.writable) {
          prop.writable = true;
        }

        if (desc.enumerable) {
          prop.enumerable = true;
        }

        if (desc.configurable) {
          prop.configurable = true;
        }

        return prop;
      };

      _recurse = function recurse(value) {
        // eslint-disable-line prefer-const,no-shadow
        if (value === null) {
          return null;
        }

        var builtinName = builtinsByValue.get(value);

        if (builtinName) {
          return {
            name: builtinName,
            type: 'builtin'
          };
        }

        var type = _typeof(value);

        switch (type) {
          case 'undefined':
            return {
              name: 'undefined',
              type: 'builtin'
            };

          case 'boolean':
            return value;

          case 'number':
            return value;

          case 'bigint':
            return {
              string: value.toString(),
              type: 'bigint'
            };

          case 'string':
            return value;

          case 'symbol':
            {
              var id = _this.internSymbol(value);

              return {
                description: value.description,
                id: id,
                type: type
              };
            }

          case 'function':
          case 'object':
            {
              var _id = _this.internObject(value);

              if (!refIds) {
                refIds = new Set();
              }

              if (refIds.has(_id)) {
                return {
                  id: _id,
                  type: 'ref'
                };
              }

              refIds.add(_id);
              var result = {
                id: _id,
                type: 'object'
              };
              var prototype = Object.getPrototypeOf(value);
              var propNames = Object.getOwnPropertyNames(value);
              var propSyms = Object.getOwnPropertySymbols(value);
              var impliedPrototype = Object.prototype;
              var skipLengthProp = false;

              if (_typeof(value) === 'object' && prototype === Array.prototype) {
                var lengthDesc = Object.getOwnPropertyDescriptor(value, 'length');
                var lengthIndex = propNames.indexOf('length');

                if (lengthDesc // eslint-disable-next-line eqeqeq
                && propNames[lengthIndex - 1] == lengthDesc.value - 1 && lengthDesc.value >= 0) {
                  result.type = 'array';
                  impliedPrototype = Array.prototype;
                  skipLengthProp = true;
                }
              } else if (typeof value === 'function') {
                impliedPrototype = Function.prototype;
                result.type = 'function';
                result.source = Function.prototype.toString.apply(value);
              }

              if (prototype !== impliedPrototype) {
                result.prototype = _recurse(prototype);
              }

              propNames.forEach(function (name) {
                if (skipLengthProp && name === 'length') {
                  return;
                }

                var desc = Object.getOwnPropertyDescriptor(value, name);
                var newName = ".".concat(name);
                result[newName] = encodeProp(desc);
              });
              propSyms.forEach(function (sym) {
                var newName;
                var symBuiltinName = builtinsByValue.get(sym);

                if (symBuiltinName) {
                  newName = "@".concat(symBuiltinName);
                } else {
                  var symId = _this.internSymbol(sym);

                  if (sym.description === undefined) {
                    newName = "~".concat(symId);
                  } else {
                    newName = "~".concat(symId, "|").concat(sym.description);
                  }
                }

                var desc = Object.getOwnPropertyDescriptor(value, sym);
                result[newName] = encodeProp(desc);
              });
              return result;
            }

          default:
            return {
              type: 'unknown',
              "typeof": type
            };
        }
      };

      return _recurse(value);
    }
  }]);

  return Encoder;
}();

export { Encoder as default };